import { RaidPlanActor, RaidPlanCast } from 'app/frontend/types/custom/RaidPlan'
import { EncounterPhase } from 'app/frontend/types/custom/EncounterEvents'
import Difficulty from 'app/frontend/types/custom/Difficulty'
import encounters from '../../../../../../../../config/static_data/encounters.json'

type EncounterPhaseMappingRow = {
  id: number
  wcl_to_timeline_phases?: { [difficulty: string]: number[][] }
}

const encounterWclToTimelinePhasesById = (
  encounters as EncounterPhaseMappingRow[]
).reduce<Record<number, { [difficulty: string]: number[][] }>>(
  (acc, encounter) => {
    if (encounter.wcl_to_timeline_phases) {
      acc[encounter.id] = encounter.wcl_to_timeline_phases
    }
    return acc
  },
  {},
)

export const resolveWclToTimelinePhases = (
  mappings: wowaudit.Encounter['wclToTimelinePhases'] | undefined,
  difficulty: Difficulty,
) => {
  if (!mappings) return undefined
  if (difficulty === 'mythic') return mappings.mythic ?? mappings.heroic
  return mappings.heroic ?? mappings.mythic
}

export const getEncounterWclToTimelinePhases = (
  encounterId: number,
  difficulty: Difficulty,
) =>
  resolveWclToTimelinePhases(
    encounterWclToTimelinePhasesById[encounterId],
    difficulty,
  )

export type KillPhaseSegment = {
  wcl_id: number
  start: number
  duration: number
}

export type KillPhaseSpan = {
  start: number
  duration: number
}

const readKillPhaseSegment = (value: unknown): KillPhaseSegment | null => {
  if (typeof value !== 'object' || value === null) return null
  const segment = value as Record<string, unknown>
  const wclId = segment.wcl_id ?? segment.wclId
  if (
    typeof wclId !== 'number' ||
    typeof segment.start !== 'number' ||
    typeof segment.duration !== 'number'
  ) {
    return null
  }

  return { wcl_id: wclId, start: segment.start, duration: segment.duration }
}

export const getKillPhaseSegments = (
  kill: wowaudit.RaidReportTopExecutionKill,
) => {
  const timeline = kill.timeline as { phases?: unknown }
  if (!Array.isArray(timeline?.phases)) return []
  return timeline.phases.flatMap((value) => {
    const segment = readKillPhaseSegment(value)
    return segment ? [segment] : []
  })
}

const mappingEntriesMatch = (left: number[], right?: number[]) => {
  if (!right || left.length !== right.length) return false
  return left.every((value, index) => value === right[index])
}

export const resolveKillPhaseSpans = ({
  segments,
  staticPhases,
  mapping,
}: {
  segments: KillPhaseSegment[]
  staticPhases: EncounterPhase[]
  mapping?: number[][] | null
}): Array<KillPhaseSpan | null> | null => {
  if (!mapping || mapping.length === 0) return null
  if (mapping.length !== staticPhases.length) return null
  if (mapping.some((entry) => !Array.isArray(entry) || entry.length === 0)) {
    return null
  }

  const spans: Array<KillPhaseSpan | null> = staticPhases.map(() => null)
  let segmentIndex = 0
  let mappingIndex = 0

  while (mappingIndex < mapping.length) {
    const entry = mapping[mappingIndex]
    if (!entry) return null

    const groupIndices = [mappingIndex]
    if (entry.length === 1) {
      while (
        mappingIndex + groupIndices.length < mapping.length &&
        mappingEntriesMatch(entry, mapping[mappingIndex + groupIndices.length])
      ) {
        groupIndices.push(mappingIndex + groupIndices.length)
      }
    }

    if (segmentIndex >= segments.length) break

    const consumed: KillPhaseSegment[] = []
    for (let offset = 0; offset < entry.length; offset += 1) {
      if (segmentIndex >= segments.length) break
      const segment = segments[segmentIndex]
      if (!segment) break
      consumed.push(segment)
      segmentIndex += 1
    }

    if (consumed.length === 0) break
    if (consumed.length < entry.length) {
      if (segmentIndex < segments.length) return null
    }

    const first = consumed[0]
    const last = consumed[consumed.length - 1]
    if (!first || !last) break
    const start = first.start
    const totalDuration = last.start + last.duration - start

    if (groupIndices.length === 1) {
      spans[groupIndices[0] ?? mappingIndex] = {
        start,
        duration: totalDuration,
      }
    } else {
      const weights = groupIndices.map((index) =>
        Math.max(staticPhases[index]?.duration ?? 0, 0),
      )
      const weightSum = weights.reduce((sum, weight) => sum + weight, 0)
      let running = start
      groupIndices.forEach((phaseIndex, groupOffset) => {
        const duration =
          groupOffset === groupIndices.length - 1
            ? start + totalDuration - running
            : weightSum > 0
            ? (totalDuration * (weights[groupOffset] ?? 0)) / weightSum
            : totalDuration / groupIndices.length
        spans[phaseIndex] = { start: running, duration }
        running += duration
      })
    }

    mappingIndex += groupIndices.length
  }

  return spans
}

const LAST_PHASE_IMPORT_PADDING_SECONDS = 30

export const adoptedPhaseDurations = (
  staticPhases: EncounterPhase[],
  spans: Array<KillPhaseSpan | null>,
) => {
  const lastReachedIndex = spans.reduce(
    (last, span, index) => (span ? index : last),
    -1,
  )
  return staticPhases.reduce<Record<string, number>>((acc, phase, index) => {
    const span = spans[index]
    if (!span) {
      acc[phase.id] = 0
      return acc
    }
    const observed = Math.max(Math.round(span.duration), 0)
    const maxDuration = Math.max(Math.round(phase.duration), 0)
    acc[phase.id] =
      index === lastReachedIndex
        ? Math.min(observed + LAST_PHASE_IMPORT_PADDING_SECONDS, maxDuration)
        : observed
    return acc
  }, {})
}

const spanContains = (span: KillPhaseSpan, at: number, isLast: boolean) => {
  const end = span.start + span.duration
  if (isLast) return at >= span.start && at <= end
  return at >= span.start && at < end
}

const resolveSpanIndex = (at: number, spans: Array<KillPhaseSpan | null>) => {
  const reachedIndices = spans.flatMap((span, index) => (span ? [index] : []))
  const lastReached = reachedIndices[reachedIndices.length - 1]
  for (const index of reachedIndices) {
    const span = spans[index]
    if (!span) continue
    if (spanContains(span, at, index === lastReached)) return index
  }
  return -1
}

export const remapKillActors = ({
  actors,
  spans,
  phaseStarts,
  adoptedDurations,
  phases,
}: {
  actors: RaidPlanActor[]
  spans: Array<KillPhaseSpan | null>
  phaseStarts: number[]
  adoptedDurations: number[]
  phases: EncounterPhase[]
}): RaidPlanActor[] => {
  const remapCast = (cast: RaidPlanCast): RaidPlanCast => {
    const phaseIndex = resolveSpanIndex(cast.at, spans)
    const span = phaseIndex >= 0 ? spans[phaseIndex] : undefined
    if (!span) return { ...cast }

    const offset = cast.at - span.start
    const nextCast: RaidPlanCast = {
      ...cast,
      at: Math.floor((phaseStarts[phaseIndex] ?? 0) + offset),
    }
    const phaseDuration = adoptedDurations[phaseIndex] ?? 0
    if (offset >= phaseDuration) {
      nextCast.hidden = true
      nextCast.hidden_phase_id = phases[phaseIndex]?.id
    } else {
      delete nextCast.hidden
      delete nextCast.hidden_phase_id
    }
    return nextCast
  }

  return actors.map((actor) => ({
    ...actor,
    casts: actor.casts.map(remapCast),
  }))
}
